import React from 'react';
import { useQuery } from '@apollo/client';

import { GET_PRODUCT_IMAGES } from 'connectors/queries/product';

import { Flex } from '@Calix-Commerce/design-system/layout';
import { PrimaryTable, ProductImage } from '@Calix-Commerce/design-system/components';

import { useFormatPrice } from 'utils/hooks';
import { getShippedQuantity } from 'pages/Order/helpers';

import { TableRow } from './styledComponents';

const SummaryContent = ({
  image,
  displayLineNumber,
  partDescription,
  partNumber,
  unitSellPrice,
  shippedQuantity,
  totalOrderedQty,
  extendedSellPrice,
}) => (
  <>
    <td>{displayLineNumber}</td>
    <td>
      <Flex>
        <ProductImage src={image} alt={partDescription}></ProductImage>
        <div>
          <div>{partNumber}</div>
          <div style={{ fontWeight: 'bold', marginTop: '0.8rem' }}>{partDescription}</div>
        </div>
      </Flex>
    </td>
    <td>{unitSellPrice}</td>
    <td>{totalOrderedQty}</td>
    <td>{shippedQuantity}</td>
    <td>{extendedSellPrice}</td>
  </>
);

const SummaryRow = ({ lineData, hasDarkBackground, partNumbers, isExtendedWarranty = false }) => {
  const formatPrice = useFormatPrice();

  const { data } = useQuery(GET_PRODUCT_IMAGES, {
    variables: {
      input: {
        partNumbers: partNumbers,
        primaryOnly: true,
        sizes: ['Small'],
      },
    },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const getImage = (partNumber) => {
    if (data?.getProductImages) {
      const imageData = data.getProductImages.find((item) => item.partNumber === partNumber);
      return imageData?.imageAbsoluteUrl;
    }

    return null;
  };

  const { partNumber, displayLineNumber, unitSellPrice, totalOrderedQty, extendedSellPrice } =
    isExtendedWarranty ? lineData.warrantyLine : lineData;

  return (
    <TableRow darkBackground={hasDarkBackground}>
      <SummaryContent
        partNumber={partNumber}
        totalOrderedQty={totalOrderedQty}
        shippedQuantity={getShippedQuantity(isExtendedWarranty ? lineData.warrantyLine : lineData)}
        // Always use the main part number image, even if we're presenting a warranty line
        image={getImage(lineData.partNumber)}
        partDescription={isExtendedWarranty ? 'Extended Warranty' : lineData.partDescription}
        displayLineNumber={displayLineNumber}
        unitSellPrice={formatPrice(unitSellPrice)}
        extendedSellPrice={formatPrice(extendedSellPrice)}
      />
    </TableRow>
  );
};

const SummaryTable = ({ linesData = [] }) => {
  const partNumbers = linesData.map((lineData) => lineData.partNumber);

  return (
    <PrimaryTable>
      <thead>
        <tr>
          <th style={{ width: '1rem' }}>#</th>
          <th>Item</th>
          <th>Unit Price</th>
          <th>Ordered Qty</th>
          <th>Shipped Qty</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {linesData.map((lineData, index) => {
          return (
            <React.Fragment
              key={lineData.warrantyLine ? lineData.warrantyLine.lineId : lineData.lineId}
            >
              <SummaryRow
                hasDarkBackground={index % 2 !== 0}
                lineData={lineData}
                partNumbers={partNumbers}
              />
              {lineData.warrantyLine ? (
                <SummaryRow
                  hasDarkBackground={index % 2 !== 0}
                  lineData={lineData}
                  partNumbers={partNumbers}
                  isExtendedWarranty={true}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </tbody>
    </PrimaryTable>
  );
};

export { SummaryTable };
